import { useCallback, useState } from 'react';

import { useStepEditorContext } from 'src/store/step-editor';
import { useCurrentOrganization } from 'src/store/organization';
import { apiCreateHtmlArtifactFromSource, apiUploadHtmlArtifact } from 'src/utils/journeyApi';
import {
  HTML_BLOCK_MAX_BYTES,
  htmlSourceByteLength,
  looksLikeHtmlSource,
} from 'src/editor/content-creation/helpers/block-types/html';
import { CONTENT_TYPE_FILE } from 'src/editor/constants';

// The artifact endpoint answers with the stored object rather than the S3
// credentials the direct-upload path returns, so the response has to be
// reshaped into what the step editor's submit already expects
// (use-submit-step-form.ts reads url, s3_key and name off uploadedS3Asset).
type StoredArtifact = {
  url: string;
  s3_key: string;
  content_type: string;
  name: string;
  size: number;
};

const toUploadedS3Asset = (artifact: StoredArtifact) => ({
  url: artifact.url,
  s3_key: artifact.s3_key,
  name: artifact.name,
  type: artifact.content_type,
});

/**
 * Uploads or pastes an HTML artifact from the step editor (JOU-40).
 *
 * Deliberately not part of useFileUploader. That hook uploads straight from the
 * browser to S3 with federated credentials, which lets the client pick the
 * stored Content-Type — acceptable for a PDF, not for a document we then serve
 * as text/html from its own origin. HTML goes through the API so the server
 * decides what is stored, and that means a different request shape, a different
 * error surface, and no getFileMetadata step.
 */
export const useHtmlArtifactUploader = () => {
  const { currentOrganization } = useCurrentOrganization();
  // Setters are read off the store at call time rather than subscribed to.
  // Destructuring the store here subscribes this hook to every write it makes,
  // which re-renders the panel mid-upload and re-runs its effects.
  const name = useStepEditorContext((state) => state.name);

  const [error, setError] = useState('');

  const applyArtifact = useCallback(
    (artifact: StoredArtifact) => {
      // Stored as a file asset, so the step behaves like any other upload from
      // here on. The player tells it apart by file_asset.content_type and
      // renders it in a sandboxed frame rather than as a link card.
      const store = useStepEditorContext.getState();
      store.setContentType(CONTENT_TYPE_FILE);
      store.setContentName(artifact.name);
      if (!name) {
        const dot = artifact.name.lastIndexOf('.');
        store.setName(dot > 0 ? artifact.name.slice(0, dot) : artifact.name);
      }
      store.setUploadedS3Asset(toUploadedS3Asset(artifact));
    },
    [name]
  );

  const uploadHtmlFile = useCallback(
    async (file: File) => {
      if (!currentOrganization) {
        return null;
      }
      if (file.size > HTML_BLOCK_MAX_BYTES) {
        setError('That file is too large. The limit is 5 MB.');
        return null;
      }

      setError('');
      useStepEditorContext.getState().setUploadInProgress(true);
      useStepEditorContext.getState().setUploadPercentage(0);
      // Drives the existing preview and the "uploaded" state in the panel; the
      // store maps a non-video, non-image, non-pdf file to CONTENT_TYPE_FILE,
      // which is what an artifact is.
      useStepEditorContext.getState().setUploadedFile(file);

      try {
        const artifact: StoredArtifact = await apiUploadHtmlArtifact(
          currentOrganization.id,
          file,
          useStepEditorContext.getState().setUploadPercentage
        );
        applyArtifact(artifact);
        useStepEditorContext.getState().setUploadPercentage(100);
        return artifact;
      } catch (e: any) {
        // The server's message is the useful one here — it names the actual
        // reason (too large, not HTML, could not be stored).
        setError(e?.response?.data?.message || 'That file could not be uploaded.');
        useStepEditorContext.getState().setUploadedFile(null);
        return null;
      } finally {
        useStepEditorContext.getState().setUploadInProgress(false);
      }
    },
    [currentOrganization, applyArtifact]
  );

  const submitHtmlSource = useCallback(
    async (source: string, artifactName?: string) => {
      if (!currentOrganization) {
        return null;
      }
      if (!source.trim()) {
        setError('Paste some HTML first.');
        return null;
      }
      if (!looksLikeHtmlSource(source)) {
        setError('That does not look like HTML.');
        return null;
      }
      if (htmlSourceByteLength(source) > HTML_BLOCK_MAX_BYTES) {
        setError('That is too large. The limit is 5 MB.');
        return null;
      }

      setError('');
      useStepEditorContext.getState().setUploadInProgress(true);

      try {
        const artifact: StoredArtifact = await apiCreateHtmlArtifactFromSource(
          currentOrganization.id,
          source,
          artifactName
        );
        // Pasted HTML has no File behind it, so the preview is driven from the
        // stored artifact alone. A synthetic File keeps the panel's uploaded
        // state consistent with the upload path.
        useStepEditorContext.getState().setUploadedFile(new File([source], artifact.name, { type: 'text/html' }));
        applyArtifact(artifact);
        useStepEditorContext.getState().setUploadPercentage(100);
        return artifact;
      } catch (e: any) {
        setError(e?.response?.data?.message || 'That HTML could not be saved.');
        return null;
      } finally {
        useStepEditorContext.getState().setUploadInProgress(false);
      }
    },
    [currentOrganization, applyArtifact]
  );

  return { uploadHtmlFile, submitHtmlSource, error, setError };
};
